import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ThumbDownAlt from '@mui/icons-material/ThumbDownAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUpOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';

import { AiRoleEnum } from 'data/aiConversations/aiConversationsTypes';

import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import useSpeechSynthesis from 'utils/speech/useSpeechSynthesis';
import useVoiceState from 'utils/speech/useVoiceState';

const getLastUserPrompt = (messages) => {
  for (let i = messages.size - 1; i >= 0; i--) {
    const message = messages.get(i); // Use .get() to access elements
    if (message.role === AiRoleEnum.USER) {
      return message.message;
    }
  }
  return '';
};
const getLastSystemPrompt = (messages) => {
  for (let i = messages.size - 1; i >= 0; i--) {
    const message = messages.get(i); // Use .get() to access elements
    if (message.role === AiRoleEnum.ASSISTANT) {
      return message.message;
    }
  }
  return '';
};

const feedbackConfig = {
  positive: ['Correct', 'Easy to understand', 'Complete'],
  negative: ['Incorrect', 'Hard to understand', 'Incomplete'],
};

export const ChatFeedback = ({
  messageIndex,
  setAutoConversation,
  messages,
  message,
  onFeedbackSubmit,
  onManageMemories,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedThumb, setSelectedThumb] = useState(message?.feedback?.thumb || null); // "up" or "down"
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [selectedReason, setSelectedReason] = useState(null);
  const [feedbackSubmit, setFeedbackSubmit] = useState(false);
  const [voiceState] = useVoiceState();
  const speak = useSpeechSynthesis();
  const theme = useTheme();
  const feedbackId = useRef(uuidv4());

  // Determine the reasons based on the thumb selected
  const feedbackReasons = selectedThumb === 'up' ? feedbackConfig.positive : feedbackConfig.negative;

  // Check if submit should be disabled
  const isSubmitDisabled = !selectedReason && additionalFeedback.length < 3;

  const handleReasonClick = (reason) => {
    setSelectedReason(reason);
  };

  useEffect(() => {
    if (onFeedbackSubmit && feedbackSubmit) {
      onFeedbackSubmit({
        feedbackId,
        messageIndex,
        userPrompt: getLastUserPrompt(messages),
        systemResponse: getLastSystemPrompt(messages),
        thumb: selectedThumb,
        reason: selectedReason,
        additionalFeedback,
      });
      setFeedbackSubmit(false);
    }
  }, [additionalFeedback, feedbackSubmit, messageIndex, messages, onFeedbackSubmit, selectedReason, selectedThumb]);

  const handleSubmit = () => {
    setSubmitted(true);
    setFeedbackSubmit(true);
  };

  const handleThumbClick = (e, thumb) => {
    if (selectedThumb !== thumb) {
      setSelectedReason(null);
      setAdditionalFeedback('');
      setSelectedThumb(thumb);
      setSubmitted(false);
      setExpanded(true);
      setFeedbackSubmit(true);
    }
  };

  const handleClose = () => {
    setExpanded(false);
    setSelectedReason(null);
    setAdditionalFeedback('');
  };

  const ThumbUp = selectedThumb === 'up' ? ThumbUpAlt : ThumbUpIcon;
  const ThumbDown = selectedThumb === 'down' ? ThumbDownAlt : ThumbDownIcon;

  return (
    <Stack
      id='messageActions'
      sx={{
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          border: `1px solid ${theme.palette.border.light}`,
          borderRadius: '8px',
          backgroundColor: theme.palette.background.paper,
          width: 'fit-content',
        }}
      >
        <Tooltip title={'Good Response'} arrow>
          <IconButton size='small' onClick={(e) => handleThumbClick(e, 'up')}>
            <ThumbUp fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title={'Bad Response'} arrow>
          <IconButton size='small' onClick={(e) => handleThumbClick(e, 'down')}>
            <ThumbDown fontSize='small' />
          </IconButton>
        </Tooltip>
        <Divider orientation='vertical' flexItem variant='middle' />
        <Tooltip title={'Copy'} arrow>
          <IconButton
            size='small'
            onClick={() => {
              navigator.clipboard.writeText(JSON.parse(message.message).response);
              dispatchSimpleNotification('Message copied to clipboard');
            }}
          >
            <ContentCopyOutlinedIcon fontSize='small' color='inherit' />
          </IconButton>
        </Tooltip>
        <Divider orientation='vertical' flexItem variant='middle' />
        <Tooltip title={'Read Aloud'} arrow>
          {setAutoConversation && (
            <IconButton
              size='small'
              onClick={() => {
                if (voiceState === 'SPEAKING') {
                  speak(null);
                  setAutoConversation(false);
                } else {
                  speak(message.message);
                }
              }}
            >
              {voiceState === 'SPEAKING' && <VolumeOffOutlinedIcon fontSize='small' color='inherit' />}
              {voiceState === 'IDLE' && <VolumeUpOutlinedIcon fontSize='small' color='inherit' />}
            </IconButton>
          )}
        </Tooltip>
        <Divider orientation='vertical' flexItem variant='middle' />
        <Tooltip title={'Manage Memories'} arrow>
          <IconButton size='small' onClick={onManageMemories}>
            <PsychologyIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>

      {expanded && (
        <Box>
          <Paper sx={{ padding: 2, marginTop: 1 }} elevation={24}>
            {submitted && (
              <Typography variant='bodyRegular' sx={{ textAlign: 'center' }}>
                Thank you for your feedback
              </Typography>
            )}
            {expanded && !submitted && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                  <Typography variant='paragraphMedium'>
                    Why did you choose this rating?{' '}
                    <span style={{ color: theme.palette.text.secondary }}>(optional)</span>
                  </Typography>
                  <IconButton variant='tonal' onClick={handleClose} size='small'>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, marginBottom: 1, flexWrap: 'wrap' }}>
                  {feedbackReasons.map((reason) => (
                    <Chip
                      variant='tonal'
                      key={reason}
                      label={reason}
                      clickable
                      color={selectedReason === reason ? 'primary' : 'default'}
                      onClick={() => handleReasonClick(reason)}
                      size='medium'
                      sx={{
                        ...theme.typography.captionMedium,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    />
                  ))}
                </Box>
                <TextField
                  hiddenLabel
                  placeholder='Provide additional feedback'
                  multiline
                  maxRows={3}
                  rows={2}
                  fullWidth
                  value={additionalFeedback}
                  onChange={(e) => setAdditionalFeedback(e.target.value)}
                  sx={{
                    marginBottom: 2,
                    '& .MuiInputBase-root': {
                      padding: 1,
                    },
                  }}
                  size='small'
                />
                <Button
                  variant='contained'
                  color='primary'
                  fullWidth
                  onClick={handleSubmit}
                  size='medium'
                  disabled={isSubmitDisabled} // Disable the submit button conditionally
                >
                  Submit
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Stack>
  );
};

export default ChatFeedback;

ChatFeedback.propTypes = {
  messageIndex: PropTypes.number,
  message: PropTypes.object.isRequired,
  setAutoConversation: PropTypes.func,
  messages: PropTypes.object.isRequired,
  onFeedbackSubmit: PropTypes.func,
  onManageMemories: PropTypes.func,
};
